/**
 * magazineService.js
 *
 * Full CRUD service layer for Magazine Studio.
 * Follows the same pattern as listings.ts:
 *   - isSupabaseAvailable() guard on every function
 *   - try/catch with { data, error } returns
 *   - snake_case DB ↔ camelCase frontend conversion
 *
 * AI-ready: insertBlock() allows the AI engine to programmatically
 * insert blocks into any article without a full savePost() round-trip.
 */

import { supabase, isSupabaseAvailable } from '../lib/supabaseClient';

// ── Per-call timeout guard ────────────────────────────────────────────────────
// supabase-js can wedge indefinitely on the navigator-lock path (auth refresh
// lock held by a crashed/orphaned tab). When that happens REST calls never
// resolve or reject, they just hang. That defeats savePostSafe's retry
// because there is no thrown error to catch. Wrap every await in savePost
// with a hard per-step ceiling so a hang becomes a catchable error.
const SUPABASE_STEP_TIMEOUT_MS = 8000;
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`supabase-timeout:${label} after ${ms}ms (likely navigator-lock stall, reload tab)`));
    }, ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

// ── Case helpers ───────────────────────────────────────────────────────────────
function snakeToCamel(key) {
  return key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
}
function camelToSnake(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}
function rowToCamel(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  for (const k in row) out[snakeToCamel(k)] = row[k];
  return out;
}

// ── DB field map: camelCase formData → snake_case DB columns ──────────────────
const POST_FIELD_MAP = {
  id:                  'id',
  slug:                'slug',
  title:               'title',
  excerpt:             'excerpt',
  standfirst:          'standfirst',
  categorySlug:        'category_slug',
  categoryLabel:       'category_label',
  authorData:          'author_data',        // { id, name, role, bio, avatar }
  author:              'author_data',        // alias, editor stores it as `author`
  tags:                'tags',
  readingTime:         'reading_time',
  published:           'published',
  publishedAt:         'published_at',
  updatedAt:           'updated_at',        // exposed for stable client-side ordering (Project B)
  featured:            'featured',
  trending:            'trending',
  editorsChoice:       'editors_choice',
  scheduledDate:       'scheduled_date',
  layout:              'layout',
  heroStyle:           'hero_style',
  heroHeight:          'hero_height',
  coverImage:          'cover_image',
  coverImageAlt:       'cover_image_alt',
  coverImageCredit:    'cover_image_credit',
  heroVideoUrl:        'hero_video_url',
  heroOverlayOpacity:  'hero_overlay_opacity',
  heroFocalPoint:      'hero_focal_point',
  heroTitlePosition:   'hero_title_position',
  tone:                'tone',
  seoTitle:            'seo_title',
  metaDescription:     'meta_description',
  ogTitle:             'og_title',
  ogDescription:       'og_description',
  ogImage:             'og_image',
  // Editorial workflow (added 20260404)
  workflowStatus:      'workflow_status',
  statusChangedAt:     'status_changed_at',
  reviewedBy:          'reviewed_by',
  // Feature flags (added 20260404)
  isFeatured:          'is_featured',
  featuredUntil:       'featured_until',
  homepageFeature:     'homepage_feature',
  categoryFeature:     'category_feature',
  // editorsChoice already mapped above → 'editors_choice'
  // AI Writer fields (added 20260404)
  aiTopic:             'ai_topic',
  aiTone:              'ai_tone',
  aiWordCount:         'ai_word_count',
  aiOutline:           'ai_outline',
  aiGenerated:         'ai_generated',
  aiLastGeneratedAt:   'ai_last_generated_at',
  aiMetadata:          'ai_metadata',
  // Editorial system fields (added 20260427)
  templateType:            'template_type',
  featuredLabel:           'featured_label',
  editorialPriority:       'editorial_priority',
  relatedListingIds:       'related_listing_ids',
  locationTags:            'location_tags',
  styleTags:               'style_tags',
  coverImages:             'cover_images',      // hero crossfade slideshow array
  // GEO Phase 1, entity mentions for AI knowledge graph (added 20260522)
  mentionsJson:            'mentions_json',
  // GEO Phase 2, author attribution for E-E-A-T (added 20260522)
  authorId:                'author_id',
  // Multi-category (added 20260405)
  secondaryCategories:     'secondary_categories',
  // Editorial Intelligence score (added 20260405)
  contentScore:            'content_score',
  contentScoreGrade:       'content_score_grade',
  contentScoreBreakdown:   'content_score_breakdown',
  contentScoreUpdatedAt:   'content_score_updated_at',
};

// Keys NOT written to magazine_posts as top-level columns. These fields are
// either stored elsewhere (blocks table, ai_metadata jsonb) or are pure UI
// state not meant to persist.
//
// Two piggyback fields live inside ai_metadata jsonb and are explicitly lifted
// back out in mapPostFromDb:
//   • galleryImages  , array of gallery image urls for the hero slider
//   • coverImageFocal, focal point object { x, y } for cover image cropping
// Keeping them out of POST_FIELD_MAP avoids a schema migration; lifting them
// on read means the editor consumes them as plain top-level formData fields.
const POST_EXCLUDED_KEYS = new Set(['content', '_lastEdited', 'date', 'category',
  'coverImageFocal', 'relatedPosts', 'heroTextAlign', 'galleryImages', 'heroCaption']);

function mapPostToDb(formData) {
  const row = {};
  for (const [camel, snake] of Object.entries(POST_FIELD_MAP)) {
    if (POST_EXCLUDED_KEYS.has(camel)) continue;
    const val = formData[camel];
    if (val === undefined) continue;
    row[snake] = val;
  }
  // Normalise author: editor stores it as `author` (object), DB wants author_data
  if (formData.author && !row.author_data) {
    row.author_data = formData.author;
  }
  // Normalise category: static seed posts use `category` (legacy field) and never
  // set `categorySlug`. Without this, persisting a seed silently drops the category
  // and the article comes back with no category_slug, which then blocks publish.
  // If categorySlug is missing but `category` is set, promote it.
  if (!row.category_slug && formData.category) {
    row.category_slug = formData.category;
  }
  if (!row.category_label && formData.categoryLabel) {
    row.category_label = formData.categoryLabel;
  }
  // Piggyback galleryImages + coverImageFocal onto ai_metadata jsonb so these
  // persist without a schema migration. Base the merge on formData.aiMetadata
  // directly (not row.ai_metadata, which is the same data routed through the
  // generic field-map loop above) so the intent is explicit and future keys
  // added to formData.aiMetadata, aiOutline, aiTopic, aiLastGeneratedAt,
  // etc., are always preserved by this piggyback branch instead of relying
  // on the ordering of writes inside `row`.
  //
  // IMPORTANT partial-save caveat: Supabase UPDATE replaces an entire jsonb
  // column, not individual keys. If a caller ever invokes savePost with a
  // formData that has galleryImages or coverImageFocal but NO aiMetadata,
  // this merge will overwrite any aiOutline/aiTopic/etc. currently in the
  // DB with just the piggyback keys. All current call sites derive formData
  // from the loaded post (which includes the full aiMetadata object), so
  // this is safe today, but partial-update helpers MUST pass the existing
  // aiMetadata through if they touch galleryImages or coverImageFocal.
  const hasGallery = Array.isArray(formData.galleryImages);
  const hasFocal   = formData.coverImageFocal !== undefined;
  const hasCaption = typeof formData.heroCaption === 'string';
  if (hasGallery || hasFocal || hasCaption) {
    const base = (formData.aiMetadata && typeof formData.aiMetadata === 'object') ? formData.aiMetadata : {};
    const merged = { ...base };
    if (hasGallery) merged.galleryImages   = formData.galleryImages;
    if (hasFocal)   merged.coverImageFocal = formData.coverImageFocal;
    if (hasCaption) merged.heroCaption     = formData.heroCaption;
    row.ai_metadata = merged;
  }
  // Ensure published_at is set when publishing
  if (row.published && !row.published_at) {
    row.published_at = new Date().toISOString();
  }
  return row;
}

function mapPostFromDb(row) {
  const c = rowToCamel(row);
  // Remap snake remnants the generic converter misses
  c.author      = c.authorData || null;
  c.content     = [];    // blocks fetched separately, merged by fetchPostBySlug
  c.readingTime = c.readingTime ?? 5;
  c.tags        = c.tags ?? [];
  // Mirror categorySlug into the legacy `category` field. The editor's primary
  // category <select> is bound to formData.category, so without this round-trip
  // the dropdown would show empty after a save → reload, and publish() would
  // be blocked by the "no category" guard.
  if (c.categorySlug && !c.category) c.category = c.categorySlug;
  // Lift ai_metadata.galleryImages + ai_metadata.coverImageFocal back into
  // their top-level formData slots so the editor's slider and focal-point
  // UI can read them directly without knowing about the piggyback.
  const meta = (c.aiMetadata && typeof c.aiMetadata === 'object') ? c.aiMetadata : null;
  c.galleryImages   = meta && Array.isArray(meta.galleryImages) ? meta.galleryImages : [];
  c.coverImageFocal = meta && meta.coverImageFocal ? meta.coverImageFocal : null;
  c.heroCaption     = meta && typeof meta.heroCaption === 'string' ? meta.heroCaption : '';
  return c;
}

// ── Slug collision resolution ──────────────────────────────────────────────────
/**
 * Ensure the slug is unique in magazine_posts.
 * If a collision exists for a DIFFERENT id, append -2, -3, etc.
 * Returns { slug, changed }, callers should surface a warning if changed=true.
 */
async function resolveSlug(slug, excludeId = null) {
  if (!isSupabaseAvailable()) return { slug, changed: false };
  let candidate = slug;
  let n = 2;
  while (true) {
    let q = supabase.from('magazine_posts').select('id').eq('slug', candidate);
    if (excludeId) q = q.neq('id', excludeId);
    const { data } = await q.maybeSingle();
    if (!data) return { slug: candidate, changed: candidate !== slug };
    candidate = `${slug}-${n++}`;
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// POSTS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch post summaries (no blocks).
 * filters: { published?: boolean, featured?: boolean, category_slug?: string }
 */
export async function fetchPosts(filters = {}) {
  if (!isSupabaseAvailable()) return { data: [], error: null };
  try {
    let q = supabase.from('magazine_posts').select('*');
    if (filters.published !== undefined) q = q.eq('published', filters.published);
    if (filters.featured  !== undefined) q = q.eq('featured',  filters.featured);
    if (filters.category_slug)           q = q.eq('category_slug', filters.category_slug);
    // Total order: primary key + `id` unique tiebreaker so DB delivery order can't drift
    // the downstream feature/secondary/slice selection (prerender determinism, Project B).
    q = q.order('updated_at', { ascending: false }).order('id', { ascending: true });
    const { data, error } = await q;
    if (error) throw error;
    return { data: (data || []).map(mapPostFromDb), error: null };
  } catch (err) {
    console.error('[magazineService] fetchPosts:', err);
    return { data: [], error: err };
  }
}

/**
 * Fetch a single post by slug, including its blocks reconstructed as content[].
 */
// Admin-only: returns any post regardless of published state. Used by admin
// dashboard preview and MagazineStudio. Do NOT use on public pages.
export async function fetchPostBySlug(slug) {
  if (!isSupabaseAvailable()) return { data: null, error: null };
  try {
    const { data: post, error: postErr } = await supabase
      .from('magazine_posts')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (postErr) throw postErr;
    if (!post) return { data: null, error: null };

    const { data: blocks, error: blocksErr } = await supabase
      .from('magazine_blocks')
      .select('*')
      .eq('post_id', post.id)
      .order('block_order', { ascending: true });
    if (blocksErr) throw blocksErr;

    const mapped = mapPostFromDb(post);
    mapped.content = (blocks || []).map(b => ({ type: b.block_type, ...b.block_content }));
    return { data: mapped, error: null };
  } catch (err) {
    console.error('[magazineService] fetchPostBySlug:', err);
    return { data: null, error: err };
  }
}

// Public-safe: only returns published posts. Draft content is filtered at DB level.
// Use this on all public-facing article pages.
export async function fetchPublishedPostBySlug(slug) {
  if (!isSupabaseAvailable()) return { data: null, error: null };
  try {
    const { data: post, error: postErr } = await supabase
      .from('magazine_posts')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();
    if (postErr) throw postErr;
    if (!post) return { data: null, error: null };

    const { data: blocks, error: blocksErr } = await supabase
      .from('magazine_blocks')
      .select('*')
      .eq('post_id', post.id)
      .order('block_order', { ascending: true });
    if (blocksErr) throw blocksErr;

    const mapped = mapPostFromDb(post);
    mapped.content = (blocks || []).map(b => ({ type: b.block_type, ...b.block_content }));
    return { data: mapped, error: null };
  } catch (err) {
    console.error('[magazineService] fetchPublishedPostBySlug:', err);
    return { data: null, error: err };
  }
}

/**
 * Save (upsert) a post and replace its blocks atomically.
 * Returns { data: savedPost, error, slugChanged, resolvedSlug }
 */
export async function savePost(formData) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase not configured') };
  // Per-step timing. When a save hangs past the editor's 20s ceiling, the
  // console shows exactly which Supabase round-trip was slow (slug lookup,
  // slug resolve, upsert, blocks read, blocks delete, blocks insert). Cheap
  //, one label per step, 0 cost on happy path.
  const t0 = performance.now();
  const mark = (label) => console.log(`[magazineService.savePost] ${label} +${Math.round(performance.now() - t0)}ms`);
  try {
    // Detect whether the incoming ID is a real DB UUID
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let isDbId = formData.id && UUID_RE.test(formData.id);

    // For non-UUID IDs (seed/static articles): check if a DB record already exists
    // with this slug, if so, update it instead of inserting a duplicate each session
    let existingDbId = null;
    if (!isDbId && formData.slug) {
      mark('slug-lookup start');
      const { data: existing } = await withTimeout(
        supabase.from('magazine_posts').select('id').eq('slug', formData.slug).maybeSingle(),
        SUPABASE_STEP_TIMEOUT_MS,
        'slug-lookup',
      );
      mark('slug-lookup end');
      if (existing?.id) { existingDbId = existing.id; isDbId = true; }
    }
    const effectiveId = isDbId ? (existingDbId || formData.id) : null;

    // 1. Resolve slug collisions
    mark('resolveSlug start');
    const { slug: resolvedSlug, changed: slugChanged } = await resolveSlug(
      formData.slug || slugify(formData.title || 'untitled'),
      effectiveId
    );
    mark('resolveSlug end');

    // 2. Build DB row
    const row = mapPostToDb({ ...formData, slug: resolvedSlug });
    row.updated_at = new Date().toISOString();
    // id is never sent in the SET clause, Postgres generates it on INSERT,
    // and UPDATE uses .eq('id', effectiveId) not an explicit id field.
    delete row.id;

    // 3. Upsert post
    let savedPost;
    if (isDbId) {
      // Update existing (real UUID or found-by-slug)
      mark('update start');
      const { data, error } = await withTimeout(
        supabase.from('magazine_posts').update(row).eq('id', effectiveId).select().single(),
        SUPABASE_STEP_TIMEOUT_MS,
        'update',
      );
      mark('update end');
      if (error) throw error;
      savedPost = data;
    } else {
      // Insert new
      mark('insert start');
      const { data, error } = await withTimeout(
        supabase.from('magazine_posts').insert([row]).select().single(),
        SUPABASE_STEP_TIMEOUT_MS,
        'insert',
      );
      mark('insert end');
      if (error) throw error;
      savedPost = data;
    }

    // 4. Replace blocks, try atomic RPC first, fall back to INSERT-first pattern.
    //
    // The atomic RPC (replace_magazine_blocks) wraps DELETE+INSERT in a single
    // Postgres transaction, if INSERT fails, DELETE is rolled back and the
    // old content remains intact. This is the cleanest path. If the RPC isn't
    // installed yet (migration 20260427_atomic_blocks_replace.sql), we fall
    // back to the JS-side INSERT-first ordering below.
    const content = formData.content || [];
    let blocksPersisted = false;
    let usedRpc = false;
    if (content.length > 0) {
      const blocksPayload = content.map((block, i) => {
        const { type: blockType, ...blockContent } = block;
        return { block_type: blockType, block_order: i * 10, block_content: blockContent };
      });
      mark('blocks-rpc start');
      const { error: rpcErr } = await withTimeout(
        supabase.rpc('replace_magazine_blocks', { p_post_id: savedPost.id, p_blocks: blocksPayload }),
        SUPABASE_STEP_TIMEOUT_MS,
        'blocks-rpc',
      );
      mark('blocks-rpc end');
      if (!rpcErr) {
        blocksPersisted = true;
        usedRpc = true;
      } else {
        // Function not installed (PGRST202 / 404) → fall through to JS pattern.
        // Other errors (RLS, constraint, etc.) → also fall through; the JS
        // INSERT-first path will surface a detailed error.
        const isMissing = /function .* does not exist|PGRST202|Not Found/i.test(rpcErr.message || '');
        if (!isMissing) {
          console.warn('[magazineService] replace_magazine_blocks RPC failed; falling back to INSERT-first:', rpcErr);
        }
      }
    } else {
      // Empty content path: still need to clear old blocks. Try RPC, fall back.
      mark('blocks-rpc-empty start');
      const { error: rpcErr } = await withTimeout(
        supabase.rpc('replace_magazine_blocks', { p_post_id: savedPost.id, p_blocks: [] }),
        SUPABASE_STEP_TIMEOUT_MS,
        'blocks-rpc-empty',
      );
      mark('blocks-rpc-empty end');
      if (!rpcErr) {
        blocksPersisted = true;
        usedRpc = true;
      }
    }

    // ── Fallback: INSERT-FIRST then DELETE (only when RPC unavailable) ─────
    //
    // The previous flow was DELETE→INSERT which had a window where, if the
    // INSERT failed, the user's content was already gone. The restore-from-
    // backup path was unreliable (same constraints that broke the insert
    // would break the restore). To eliminate that data-loss path entirely:
    //
    //   1. Read existing block IDs (to know what to delete later)
    //   2. INSERT new blocks at a HIGH block_order range (above current max)
    //      so they coexist with old blocks during the brief overlap window
    //      without colliding. block_order has no unique constraint, gaps
    //      and overlaps are explicitly allowed by the schema.
    //   3. DELETE old blocks by ID (never touches the just-inserted blocks)
    //
    // Failure modes are now fail-safe:
    //   • INSERT fails  → old blocks intact, return clean error
    //   • DELETE fails  → new blocks saved, old duplicates briefly visible
    //                     until the NEXT save reads them and replaces them.
    //                     No data loss; at worst a brief render glitch.
    //   • Both succeed → clean replacement, no overlap window.
    if (!usedRpc) {
      mark('blocks-read start');
      const { data: existingBlocks } = await withTimeout(
        supabase.from('magazine_blocks').select('id, block_order').eq('post_id', savedPost.id).order('block_order', { ascending: true }),
        SUPABASE_STEP_TIMEOUT_MS,
        'blocks-read',
      );
      mark('blocks-read end');
      const oldBlockIds = (existingBlocks || []).map(b => b.id);
      const maxOldOrder = (existingBlocks || []).reduce((m, b) => Math.max(m, b.block_order || 0), 0);

      if (content.length > 0) {
        // Place new blocks above any existing block_order so the in-flight
        // overlap is render-safe. Gap of +1000 leaves room for AI mid-insertion.
        const baseOrder = maxOldOrder + 1000;
        const blockRows = content.map((block, i) => {
          const { type: blockType, ...blockContent } = block;
          return {
            post_id:       savedPost.id,
            block_type:    blockType,
            block_order:   baseOrder + i * 10,
            block_content: blockContent,
          };
        });
        mark('blocks-insert start');
        const { error: blocksErr } = await withTimeout(
          supabase.from('magazine_blocks').insert(blockRows),
          SUPABASE_STEP_TIMEOUT_MS,
          'blocks-insert',
        );
        mark('blocks-insert end');
        if (blocksErr) {
          // INSERT failed, old blocks are still in DB, untouched. Surface the
          // full error (code + message + details) so the caller can see why.
          console.error('[magazineService] Block insert failed (old blocks intact):', blocksErr);
          const detail = [blocksErr.code, blocksErr.message, blocksErr.details, blocksErr.hint].filter(Boolean).join(' · ');
          return {
            data: null,
            error: new Error(`Block insert failed: ${detail || 'unknown'}`),
            slugChanged,
            resolvedSlug,
            blockInsertFailed: true,
          };
        }
        blocksPersisted = true;
      } else {
        blocksPersisted = true; // Empty content is valid
      }

      // Now safe to delete old blocks, new ones are committed (or content empty).
      if (oldBlockIds.length > 0) {
        mark('blocks-delete start');
        const { error: deleteErr } = await withTimeout(
          supabase.from('magazine_blocks').delete().in('id', oldBlockIds),
          SUPABASE_STEP_TIMEOUT_MS,
          'blocks-delete',
        );
        mark('blocks-delete end');
        if (deleteErr) {
          // Non-fatal: new blocks saved; old duplicates remain at low block_order
          // and will be cleaned on the next successful save. Data is preserved.
          console.warn('[magazineService] Old blocks delete failed; duplicates may render until next save:', deleteErr);
        }
      }
    }

    mark('done');
    const result = mapPostFromDb(savedPost);
    result.content = formData.content || [];
    return {
      data: result,
      error: null,
      slugChanged,
      resolvedSlug,
      isNewRecord: !isDbId && !existingDbId,
      blocksPersisted // Client can verify blocks were saved successfully
    };
  } catch (err) {
    mark(`threw: ${err?.message || err}`);
    console.error('[magazineService] savePost:', err);
    return { data: null, error: err, slugChanged: false, resolvedSlug: formData.slug };
  }
}

/**
 * Retry-aware savePost wrapper: Navigator Lock steal (AbortError) can cause a
 * one-off failure when multiple tabs contend for the Supabase auth lock. Same
 * lock can wedge a call entirely, magazineService surfaces those as
 * `supabase-timeout:<step>` errors (8s per step). A single retry after a short
 * delay usually clears both conditions.
 */
export async function savePostSafe(postData) {
  let result = await savePost(postData);
  const msg = result?.error?.message || '';
  if (result.error && /abort|lock.*broken|steal|supabase-timeout/i.test(msg)) {
    console.warn('[savePostSafe] transient error, retrying once:', msg);
    await new Promise(r => setTimeout(r, 500));
    result = await savePost(postData);
  }
  return result;
}

/**
 * Fire-and-forget save event logger.
 * Writes one row to editor_save_events so the admin health widget can show
 * the last N save outcomes without querying magazine_posts.
 *
 * Never throws, a logging failure must never block or affect the real save.
 */
export function logSaveEvent({ postId, postTitle, ok, errorMsg, durationMs }) {
  if (!isSupabaseAvailable()) return;
  const row = {
    post_id:    postId   || null,
    post_title: (postTitle || '').slice(0, 120),
    ok:         !!ok,
    error_msg:  errorMsg ? String(errorMsg).slice(0, 255) : null,
    duration_ms: durationMs ?? null,
    saved_at:   new Date().toISOString(),
  };
  // Best-effort insert, ignore errors, don't await
  supabase.from('editor_save_events').insert([row]).then(
    ({ error }) => { if (error) console.debug('[logSaveEvent] skipped:', error.message); }
  );
}

/**
 * Publish a post: set published=true, published_at=now()
 */
export async function publishPost(id) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    const { error } = await supabase
      .from('magazine_posts')
      .update({ published: true, published_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (err) {
    console.error('[magazineService] publishPost:', err);
    return { error: err };
  }
}

/**
 * Unpublish a post: set published=false
 */
export async function unpublishPost(id) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    const { error } = await supabase
      .from('magazine_posts')
      .update({ published: false })
      .eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (err) {
    console.error('[magazineService] unpublishPost:', err);
    return { error: err };
  }
}

/**
 * Delete a post (blocks cascade via FK).
 */
export async function deletePost(id) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    const { error } = await supabase.from('magazine_posts').delete().eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (err) {
    console.error('[magazineService] deletePost:', err);
    return { error: err };
  }
}

/**
 * AI-READY: Insert a single block at a specific order position.
 * The AI engine calls this directly to inject generated content
 * without a full savePost() round-trip.
 *
 * @param {string} postId
 * @param {string} blockType  e.g. 'paragraph', 'heading', 'image'
 * @param {number} blockOrder  e.g. 25 (between order 20 and 30)
 * @param {object} blockContent  The block payload (without `type`)
 */
export async function insertBlock(postId, blockType, blockOrder, blockContent) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase not configured') };
  try {
    const { data, error } = await supabase
      .from('magazine_blocks')
      .insert([{ post_id: postId, block_type: blockType, block_order: blockOrder, block_content: blockContent }])
      .select()
      .single();
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('[magazineService] insertBlock:', err);
    return { data: null, error: err };
  }
}

/**
 * AI-READY: Replace all blocks for a post with a new content array.
 * Used when AI generates a full article structure.
 *
 * @param {string} postId
 * @param {Array}  contentArray  Array of { type, ...blockContent }
 */
export async function replaceBlocks(postId, contentArray) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    await supabase.from('magazine_blocks').delete().eq('post_id', postId);
    if (contentArray.length > 0) {
      const rows = contentArray.map((block, i) => {
        // See savePost's block map, rename the outer destructure so a
        // nested `type` inside block content can never be stripped.
        const { type: blockType, ...blockContent } = block;
        return { post_id: postId, block_type: blockType, block_order: i * 10, block_content: blockContent };
      });
      const { error } = await supabase.from('magazine_blocks').insert(rows);
      if (error) throw error;
    }
    return { error: null };
  } catch (err) {
    console.error('[magazineService] replaceBlocks:', err);
    return { error: err };
  }
}

/**
 * Extract mentions of venues and planners from article content.
 * Used for auto-linking phase (Phase 3: Internal Linking Engine).
 *
 * @param {Object} post - article object with title, excerpt, standfirst, content[]
 * @returns {Array} array of { name, type, entityId, confidence, context }
 */
export async function extractMentionsFromPost(post) {
  if (!post) return [];

  try {
    const { findMentionsInArticle } = await import('./entityMatchingService.js');
    return findMentionsInArticle(post);
  } catch (err) {
    console.error('[magazineService] extractMentionsFromPost failed:', err);
    return [];
  }
}

/**
 * Fetch published articles with optional filtering.
 * Used by article search and recommendation engine.
 *
 * @param {Object} filters
 *   - category_slug?: string, filter by category
 *   - featured?: boolean, only featured articles
 *   - trending?: boolean, only trending articles
 *   - tags?: string[], articles matching ANY tag
 *   - limit?: number, max results (default 100)
 *   - offset?: number, pagination offset (default 0)
 *
 * Returns: { data: [], error }
 */
export async function fetchPublishedArticles(filters = {}) {
  if (!isSupabaseAvailable()) return { data: [], error: null };
  try {
    const {
      category_slug,
      featured,
      trending,
      tags,
      limit = 100,
      offset = 0,
    } = filters;

    let q = supabase
      .from('magazine_posts')
      .select('*')
      .eq('published', true);

    if (category_slug) q = q.eq('category_slug', category_slug);
    if (featured) q = q.eq('featured', true);
    if (trending) q = q.eq('trending', true);

    // Tag filtering: articles with tags array containing ANY of the provided tags
    if (tags && Array.isArray(tags) && tags.length > 0) {
      // Use overlaps operator if available, otherwise fetch all and filter client-side
      q = q.overlaps('tags', tags);
    }

    q = q.order('published_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, error } = await q;
    if (error) throw error;
    return { data: (data || []).map(mapPostFromDb), error: null };
  } catch (err) {
    console.error('[magazineService] fetchPublishedArticles:', err);
    return { data: [], error: err };
  }
}

/**
 * Full-text search over published articles.
 * Searches title, excerpt, tags, and category by substring matching.
 *
 * @param {string} query, search terms
 * @param {Object} options
 *   - limit?: number, max results (default 20)
 *   - category_slug?: string, scope to category
 *
 * Returns: { data: [], error }
 */
export async function searchArticlesFullText(query = '', options = {}) {
  if (!isSupabaseAvailable()) return { data: [], error: null };
  if (!query.trim()) return { data: [], error: null };

  try {
    const { limit = 20, category_slug } = options;
    const normalizedQuery = query.toLowerCase();

    // Fetch all published articles (or scoped to category)
    // In production, consider adding a full-text search column in Postgres
    let filters = { limit: 1000 }; // Fetch a large batch for client-side filtering
    if (category_slug) {
      filters.category_slug = category_slug;
    }

    const { data: articles, error } = await fetchPublishedArticles(filters);
    if (error) throw error;

    // Client-side filtering (title, excerpt, tags, category)
    const filtered = (articles || []).filter(article => {
      // Title match (highest priority)
      if (article.title?.toLowerCase().includes(normalizedQuery)) return true;

      // Excerpt match
      if (article.excerpt?.toLowerCase().includes(normalizedQuery)) return true;

      // Tag match
      if (Array.isArray(article.tags)) {
        if (article.tags.some(t => t.toLowerCase().includes(normalizedQuery))) {
          return true;
        }
      }

      // Category label match
      if (article.categoryLabel?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return false;
    });

    return { data: filtered.slice(0, limit), error: null };
  } catch (err) {
    console.error('[magazineService] searchArticlesFullText:', err);
    return { data: [], error: err };
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL ARTICLE LINK SUGGESTIONS
// Finds published articles related to the current article's content/keywords.
// Used by the SEO panel to suggest internal links proactively.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * suggestArticleLinks({ title, content, tags, categorySlug, currentPostId, focusKeyword })
 * Returns: Array of { id, slug, title, categoryLabel, score, matchedTerms }
 * Sorted by relevance score descending. Max 5 results.
 */
export async function suggestArticleLinks({ title, content, tags, categorySlug, currentPostId, focusKeyword }) {
  if (!isSupabaseAvailable()) return [];

  // Build a bag of terms from the current article
  const terms = new Set();
  if (focusKeyword) focusKeyword.toLowerCase().split(/\s+/).forEach(w => w.length > 3 && terms.add(w));
  if (title) title.toLowerCase().split(/\s+/).forEach(w => w.length > 3 && terms.add(w));
  if (Array.isArray(tags)) tags.forEach(t => terms.add(t.toLowerCase()));

  // Extract key nouns from content blocks (first 8 headings + intro)
  if (Array.isArray(content)) {
    content
      .filter(b => b.type === 'heading' || b.type === 'intro')
      .slice(0, 8)
      .forEach(b => {
        const txt = (b.text || '').replace(/<[^>]*>/g, '').toLowerCase();
        txt.split(/\s+/).forEach(w => w.length > 4 && terms.add(w));
      });
  }

  if (terms.size === 0) return [];

  // Fetch published articles (exclude current)
  const { data: articles } = await fetchPublishedArticles({ limit: 200 });
  if (!articles || articles.length === 0) return [];

  const stopWords = new Set(['about','their','there','these','those','which','would','could','should','being','after','before','between','through','during','without','within']);

  const scored = articles
    .filter(a => a.id !== currentPostId)
    .map(article => {
      const haystack = [
        article.title,
        article.excerpt,
        ...(Array.isArray(article.tags) ? article.tags : []),
        article.categoryLabel,
      ].filter(Boolean).join(' ').toLowerCase();

      let score = 0;
      const matchedTerms = [];

      for (const term of terms) {
        if (stopWords.has(term)) continue;
        if (haystack.includes(term)) {
          score += term.length > 6 ? 3 : 1; // longer terms = stronger signal
          matchedTerms.push(term);
        }
      }

      // Boost same category
      if (categorySlug && article.categorySlug === categorySlug) score += 2;

      return { id: article.id, slug: article.slug, title: article.title, categoryLabel: article.categoryLabel, score, matchedTerms };
    })
    .filter(a => a.score >= 3)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  return scored;
}


// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════════

const CAT_FIELD_MAP = {
  id:                    'id',
  slug:                  'slug',
  name:                  'name',
  label:                 'name',           // Alias: form may use 'label'
  description:           'description',
  heroImage:             'hero_image',
  accentColor:           'accent_color',
  heroTitle:             'hero_title',
  cardStyle:             'card_style',
  defaultCardStyle:      'card_style',     // Alias: form may use this
  sortOrder:             'sort_order',
  parentSlug:            'parent_category_slug',  // Subcategories support
  seoTitle:              'seo_title',
  seoDescription:        'seo_description',
  metaDescription:       'seo_description', // Alias
  seoKeywords:           'seo_keywords',
  // Tier 1: Core AI & Aura Integration (added 20260407)
  aiDiscoveryEnabled:    'ai_discovery_enabled',
  aiCuratorPrompt:       'ai_curator_prompt',
  editorialVoice:        'editorial_voice',
  discoveryKeywords:     'discovery_keywords',
  targetAudience:        'target_audience',
  auraPriority:          'aura_priority',
  // Tier 1: Homepage & Editorial Promotion (added 20260407)
  featuredOnHomepage:    'featured_on_homepage',
  homepageSortOrder:     'homepage_sort_order',
  isActive:              'is_active',
  icon:                  'icon',
  // Tier 2: Content Strategy (added 20260407)
  contentGuidelines:     'content_guidelines',
  featuredUntil:         'featured_until',
};

function mapCatToDb(data) {
  const row = {};
  for (const [camel, snake] of Object.entries(CAT_FIELD_MAP)) {
    if (data[camel] !== undefined) {
      let value = data[camel];

      // Normalize empty parentSlug to null (for top-level categories)
      // FK constraint on parent_category_slug requires null, not empty string
      if (camel === 'parentSlug' && (!value || value.trim() === '')) {
        value = null;
      }

      row[snake] = value;
    }
  }

  // Log payload for debugging FK constraint issues (use DEBUG_MAGAZINE env var to enable)
  if (import.meta.env.VITE_DEBUG_MAGAZINE && (data.label || data.slug)) {
    console.log('[mapCatToDb] Saving category:', {
      slug: row.slug,
      name: row.name,
      parent_category_slug: row.parent_category_slug
    });
  }

  return row;
}

/**
 * Fetch all categories.
 */
export async function fetchCategories() {
  if (!isSupabaseAvailable()) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      .from('magazine_categories')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return { data: (data || []).map(rowToCamel), error: null };
  } catch (err) {
    console.error('[magazineService] fetchCategories:', err);
    return { data: [], error: err };
  }
}

/**
 * Upsert a category by slug.
 */
export async function saveCategory(catData) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase not configured') };
  try {
    const row = mapCatToDb(catData);
    row.updated_at = new Date().toISOString();
    const { data, error } = await supabase
      .from('magazine_categories')
      .upsert([row], { onConflict: 'slug' })
      .select()
      .single();
    if (error) throw error;
    return { data: rowToCamel(data), error: null };
  } catch (err) {
    console.error('[magazineService] saveCategory:', err);
    return { data: null, error: err };
  }
}

/**
 * Fresh read of a single category by slug, the read-back half of verifiedSave.
 */
export async function fetchCategoryBySlug(slug) {
  if (!isSupabaseAvailable()) return { data: null, error: new Error('Supabase not configured') };
  try {
    const { data, error } = await supabase
      .from('magazine_categories')
      .select('*')
      .eq('slug', slug)
      .abortSignal(AbortSignal.timeout(10_000))  // fail fast, never hang a save read-back
      .maybeSingle();
    if (error) throw error;
    return { data: data ? rowToCamel(data) : null, error: null };
  } catch (err) {
    console.error('[magazineService] fetchCategoryBySlug:', err);
    return { data: null, error: err };
  }
}

/**
 * Delete a category by slug.
 */
export async function deleteCategory(slug) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    const { error } = await supabase.from('magazine_categories').delete().eq('slug', slug);
    if (error) throw error;
    return { error: null };
  } catch (err) {
    console.error('[magazineService] deleteCategory:', err);
    return { error: err };
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// HOMEPAGE CONFIG
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch the homepage sections config.
 * Returns the sections array, or null if no row exists yet.
 */
export async function fetchHomepageConfig() {
  if (!isSupabaseAvailable()) return { data: null, error: null };
  try {
    const { data, error } = await supabase
      .from('magazine_homepage')
      .select('sections')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return { data: data?.sections ?? null, error: null };
  } catch (err) {
    console.error('[magazineService] fetchHomepageConfig:', err);
    return { data: null, error: err };
  }
}

/**
 * Upsert the homepage sections config (single row pattern).
 */
export async function saveHomepageConfig(sections) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  try {
    // Check for existing row
    const { data: existing } = await supabase
      .from('magazine_homepage')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      const { error } = await supabase
        .from('magazine_homepage')
        .update({ sections, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('magazine_homepage')
        .insert([{ sections }]);
      if (error) throw error;
    }
    return { error: null };
  } catch (err) {
    console.error('[magazineService] saveHomepageConfig:', err);
    return { error: err };
  }
}

// ────────────────────────────────────────────────────────────────────────────
// M2 editorial homepage, admin-driven section config
//
// Independent of the classic /the-edit configuration. Stored on the same
// magazine_homepage row but in a separate jsonb column (`sections_m2`)
// so editing one layout never touches the other.
//
// Section shape (canonical, mirrors the migration seed):
//
//   {
//     id:      string,            // stable, ordering-independent
//     type:    'cover' | 'latest' | 'category' | 'editor_choice'
//            | 'longform' | 'banner' | 'newsletter',
//     visible: boolean,
//     label?:  string,             // optional override (used for category)
//     config:  {
//       mode?:         'auto' | 'manual',
//       articleId?:    string,     // single-pick (cover, longform, banner)
//       articleIds?:   string[],   // multi-pick (category, editor_choice)
//       featured?:     string,     // latest: 1 hero
//       supporting?:   string[],   // latest: supporting thumbs
//       categorySlug?: string,     // category: auto-mode source
//       variant?:      string,     // category: visual variant
//       count?:        number,     // auto-mode N
//       dark?:         boolean,    // category: cool-black band
//       ghostFooter?:  string,     // category dark: footer ghost word
//     },
//   }
// ────────────────────────────────────────────────────────────────────────────

// Used as the fallback when the database row is empty or unreachable.
// Mirrors the seed in 20260430_magazine_homepage_m2.sql so a clean install
// renders the same M2 we ship today.
export const DEFAULT_M2_SECTIONS = [
  { id: 'cover-1',         type: 'cover',         visible: true, config: { mode: 'auto' } },
  { id: 'latest-1',        type: 'latest',        visible: true, label: 'The Latest',             config: { mode: 'auto', count: 5 } },
  { id: 'hotels-1',        type: 'category',      visible: true, label: 'Luxury Hotels & Venues', config: { mode: 'auto', categorySlug: 'hotels',        variant: 'portrait-strip', count: 4, dark: true, ghostFooter: 'DISCOVERED' } },
  { id: 'love-stories-1',  type: 'category',      visible: true, label: 'Love Stories',           config: { mode: 'auto', categorySlug: 'real-weddings', variant: 'love-stories',   count: 5 } },
  { id: 'travel-1',        type: 'category',      visible: true, label: 'Travel',                 config: { mode: 'auto', categorySlug: 'travel',        variant: 'portrait-strip', count: 3 } },
  { id: 'editor-choice-1', type: 'editor_choice', visible: true,                                  config: { mode: 'auto', count: 6 } },
  { id: 'longform-1',      type: 'longform',      visible: true,                                  config: { mode: 'auto' } },
  { id: 'style-1',         type: 'category',      visible: true, label: 'Your Style',             config: { mode: 'auto', categorySlug: 'fashion',       variant: 'four-up',        count: 4, dark: true } },
  { id: 'home-living-1',   type: 'category',      visible: true, label: 'Home & Living',          config: { mode: 'auto', categorySlug: 'home-living',  variant: 'mosaic',         count: 5, bg: '#f1f0ea' } },
  { id: 'newsletter-1',    type: 'newsletter',    visible: true,                                  config: {} },
];

// Columns the M2 layout actually reads. Keep narrow so a homepage render
// is one round-trip with no overfetch.
const M2_POST_COLUMNS = `
  id, slug, title, standfirst, excerpt, cover_image,
  category_slug, category_label, tags, author_data,
  template_type, featured_label,
  editorial_priority, published_at, created_at,
  reading_time
`;

/**
 * Read the M2 sections config from the single magazine_homepage row.
 * Returns the saved array, or DEFAULT_M2_SECTIONS if the row is empty
 * or Supabase isn't configured.
 */
export async function fetchHomepageM2Config() {
  if (!isSupabaseAvailable()) {
    return { data: DEFAULT_M2_SECTIONS, error: null, fromDefault: true };
  }
  try {
    const { data, error } = await supabase
      .from('magazine_homepage')
      .select('sections_m2')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const sections = Array.isArray(data?.sections_m2) ? data.sections_m2 : null;
    if (!sections || sections.length === 0) {
      return { data: DEFAULT_M2_SECTIONS, error: null, fromDefault: true };
    }
    return { data: sections, error: null, fromDefault: false };
  } catch (err) {
    console.error('[magazineService] fetchHomepageM2Config:', err);
    return { data: DEFAULT_M2_SECTIONS, error: err, fromDefault: true };
  }
}

/**
 * Upsert the M2 sections config (single-row pattern, mirrors
 * saveHomepageConfig). Does not touch the classic `sections` column
 * or `layout_variant`.
 */
export async function saveHomepageM2Config(sections) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  if (!Array.isArray(sections)) return { error: new Error('sections must be an array') };
  try {
    const { data: existing } = await supabase
      .from('magazine_homepage')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      const { error } = await supabase
        .from('magazine_homepage')
        .update({ sections_m2: sections, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('magazine_homepage')
        .insert([{ sections: [], sections_m2: sections, layout_variant: 'classic' }]);
      if (error) throw error;
    }
    return { error: null };
  } catch (err) {
    console.error('[magazineService] saveHomepageM2Config:', err);
    return { error: err };
  }
}

/**
 * Read the admin-only `layout_variant` preference. Front-end /the-edit
 * does NOT read this yet, it stays on the classic layout until we
 * manually flip the route. The column is exposed only so the admin UI
 * can record which variant the editor was last working on.
 */
export async function fetchLayoutVariant() {
  if (!isSupabaseAvailable()) return { data: 'classic', error: null };
  try {
    const { data, error } = await supabase
      .from('magazine_homepage')
      .select('layout_variant')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return { data: data?.layout_variant || 'classic', error: null };
  } catch (err) {
    console.error('[magazineService] fetchLayoutVariant:', err);
    return { data: 'classic', error: err };
  }
}

export async function saveLayoutVariant(variant) {
  if (!isSupabaseAvailable()) return { error: new Error('Supabase not configured') };
  if (variant !== 'classic' && variant !== 'm2') {
    return { error: new Error(`invalid variant: ${variant}`) };
  }
  try {
    const { data: existing } = await supabase
      .from('magazine_homepage')
      .select('id')
      .limit(1)
      .maybeSingle();
    if (existing?.id) {
      const { error } = await supabase
        .from('magazine_homepage')
        .update({ layout_variant: variant, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('magazine_homepage')
        .insert([{ sections: [], sections_m2: DEFAULT_M2_SECTIONS, layout_variant: variant }]);
      if (error) throw error;
    }
    return { error: null };
  } catch (err) {
    console.error('[magazineService] saveLayoutVariant:', err);
    return { error: err };
  }
}

// ── Resolver ────────────────────────────────────────────────────────────────
// Walks an M2 sections config and hydrates each section with the actual
// post rows it should render. Manual-mode IDs are gathered up front and
// fetched in a single query; auto-mode sections then fill the remaining
// slots, skipping any post already used so the same article never
// appears twice on the page.
//
// Returns: { data: [{ ...section, posts: [...] }], error }
//
// Sections with visible: false are dropped from the output so the
// front-end can render the array linearly.

function _collectManualIds(sections) {
  const ids = new Set();
  for (const s of sections) {
    if (!s?.visible) continue;
    const c = s.config || {};
    if (c.mode !== 'manual') continue;
    if (c.articleId) ids.add(c.articleId);
    if (c.featured)  ids.add(c.featured);
    if (Array.isArray(c.articleIds)) c.articleIds.forEach(id => id && ids.add(id));
    if (Array.isArray(c.supporting)) c.supporting.forEach(id => id && ids.add(id));
  }
  return Array.from(ids);
}

async function _fetchPostsByIds(ids) {
  if (!ids.length) return new Map();
  const { data, error } = await supabase
    .from('magazine_posts')
    .select(M2_POST_COLUMNS)
    .in('id', ids)
    .eq('published', true);
  if (error) throw error;
  const byId = new Map();
  (data || []).forEach(p => byId.set(p.id, p));
  return byId;
}

async function _autoCategoryPosts({ categorySlug, count, exclude }) {
  let q = supabase
    .from('magazine_posts')
    .select(M2_POST_COLUMNS)
    .eq('published', true);
  if (categorySlug) {
    q = q.or(`category_slug.eq.${categorySlug},tags.cs.{"${categorySlug}"}`);
  }
  q = q.order('editorial_priority', { ascending: true, nullsFirst: false })
       .order('published_at', { ascending: false })
       .order('id', { ascending: true })
       .limit(Math.max(count + exclude.size, count + 4));
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).filter(p => !exclude.has(p.id)).slice(0, count);
}

async function _autoEditorChoicePosts({ count, exclude }) {
  // Posts marked with a featured_label OR with low editorial_priority
  const { data, error } = await supabase
    .from('magazine_posts')
    .select(M2_POST_COLUMNS)
    .eq('published', true)
    .order('editorial_priority', { ascending: true, nullsFirst: false })
    .order('published_at', { ascending: false })
    .order('id', { ascending: true })
    .limit(count + exclude.size + 4);
  if (error) throw error;
  return (data || []).filter(p => !exclude.has(p.id)).slice(0, count);
}

/**
 * Resolve a saved M2 sections config into render-ready data.
 *
 * @param {Object} options
 *   - sections?: section[], bypass DB fetch (used for live admin preview)
 *
 * Each returned section is shape-compatible with its config plus a
 * `posts` array. Sections with visible: false are filtered out.
 */
export async function resolveHomepageM2({ sections } = {}) {
  if (!isSupabaseAvailable()) {
    return { data: [], error: null };
  }
  try {
    const config = Array.isArray(sections) && sections.length
      ? sections
      : (await fetchHomepageM2Config()).data;

    const visibleSections = (config || []).filter(s => s?.visible);
    const manualIds = _collectManualIds(visibleSections);
    const manualMap = await _fetchPostsByIds(manualIds);

    // Track every post id used on the page so auto sections never
    // produce duplicates.
    const used = new Set();
    const resolved = [];

    for (const section of visibleSections) {
      const c   = section.config || {};
      const out = { ...section, posts: [] };

      switch (section.type) {
        case 'cover': {
          if (c.mode === 'manual' && c.articleId && manualMap.has(c.articleId)) {
            out.posts = [manualMap.get(c.articleId)];
          } else {
            const [post] = await _autoEditorChoicePosts({ count: 1, exclude: used });
            if (post) out.posts = [post];
          }
          break;
        }

        case 'latest': {
          const count = Math.max(1, c.count || 5);
          if (c.mode === 'manual') {
            const featured = c.featured && manualMap.get(c.featured);
            const supporting = (c.supporting || [])
              .map(id => manualMap.get(id))
              .filter(Boolean);
            out.posts = [featured, ...supporting].filter(Boolean).slice(0, count);
          } else {
            const { data, error } = await supabase
              .from('magazine_posts')
              .select(M2_POST_COLUMNS)
              .eq('published', true)
              .order('published_at', { ascending: false })
              .order('id', { ascending: true })
              .limit(count + used.size + 2);
            if (error) throw error;
            out.posts = (data || []).filter(p => !used.has(p.id)).slice(0, count);
          }
          break;
        }

        case 'category': {
          const count = Math.max(1, c.count || 3);
          if (c.mode === 'manual' && Array.isArray(c.articleIds)) {
            out.posts = c.articleIds.map(id => manualMap.get(id)).filter(Boolean).slice(0, count);
          } else {
            out.posts = await _autoCategoryPosts({
              categorySlug: c.categorySlug,
              count,
              exclude: used,
            });
          }
          break;
        }

        case 'editor_choice': {
          const count = Math.max(1, c.count || 6);
          if (c.mode === 'manual' && Array.isArray(c.articleIds)) {
            out.posts = c.articleIds.map(id => manualMap.get(id)).filter(Boolean).slice(0, count);
          } else {
            out.posts = await _autoEditorChoicePosts({ count, exclude: used });
          }
          break;
        }

        case 'longform':
        case 'banner': {
          if (c.mode === 'manual' && c.articleId && manualMap.has(c.articleId)) {
            out.posts = [manualMap.get(c.articleId)];
          } else {
            const [post] = await _autoEditorChoicePosts({ count: 1, exclude: used });
            if (post) out.posts = [post];
          }
          break;
        }

        case 'newsletter':
        default:
          out.posts = [];
          break;
      }

      out.posts.forEach(p => p?.id && used.add(p.id));
      resolved.push(out);
    }

    return { data: resolved, error: null };
  } catch (err) {
    console.error('[magazineService] resolveHomepageM2:', err);
    return { data: [], error: err };
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// MEDIA TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Track an uploaded file in magazine_media.
 * Called by MagazineMediaUploader after a successful Supabase Storage upload.
 * Fire-and-forget, errors are logged but not thrown.
 */
export async function trackMediaUpload(fileUrl, meta = {}) {
  if (!isSupabaseAvailable()) return;
  try {
    await supabase.from('magazine_media').insert([{
      file_url:  fileUrl,
      title:     meta.title    || null,
      alt_text:  meta.alt      || null,
      caption:   meta.caption  || null,
      credit:    meta.credit   || null,
    }]);
  } catch (err) {
    console.warn('[magazineService] trackMediaUpload (non-fatal):', err);
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Convert a title to a URL-safe slug.
 * e.g. "The Art of the Amalfi Wedding" → "the-art-of-the-amalfi-wedding"
 */
export function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
